import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { onIdTokenChanged, reload, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import AppLoading from '../components/AppLoading';
import { readEmployerSetup, syncAuthCookie, clearAuthCookie } from '../utils/authFlow';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [account, setAccount] = useState({ uid: null, profile: null, error: null });
  const accountRequest = useRef(0);

  const refreshAccount = useCallback(async () => {
    const user = auth.currentUser;
    const request = ++accountRequest.current;
    if (!user) return null;
    setAccount({ uid: null, profile: null, error: null });
    try {
      const profile = await readEmployerSetup(user.uid);
      if (request === accountRequest.current && auth.currentUser?.uid === user.uid) {
        setAccount({ uid: user.uid, profile, error: null });
      }
      return profile;
    } catch (error) {
      if (request === accountRequest.current && auth.currentUser?.uid === user.uid) {
        setAccount({ uid: user.uid, profile: null, error });
      }
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return false;
    await reload(user);
    await user.getIdToken(true);
    setEmailVerified(user.emailVerified);
    await syncAuthCookie(user);
    return user.emailVerified;
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      setCurrentUser(user);
      setEmailVerified(Boolean(user?.emailVerified));
      setLoading(false);
      if (user?.emailVerified) syncAuthCookie(user).catch(() => {});
      else clearAuthCookie();
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (currentUser?.uid && emailVerified) refreshAccount();
    else {
      accountRequest.current += 1;
      setAccount({ uid: null, profile: null, error: null });
    }
  }, [currentUser?.uid, emailVerified, refreshAccount]);

  const logout = async () => {
    try {
      await signOut(auth);
      clearAuthCookie();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    currentUser,
    loading,
    logout,
    emailVerified,
    accountProfile: account.profile,
    accountLoading: Boolean(currentUser && emailVerified && account.uid !== currentUser.uid),
    accountError: account.error,
    refreshAccount,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <AppLoading /> : children}
    </AuthContext.Provider>
  );
}; 
